import { useTranslation } from "react-i18next";
import { Col, Row, Select } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useMemo } from "react";

interface BirthdaySelectProps {
  value?: Dayjs | null;
  onChange?: (value: Dayjs | null) => void;
  disabled?: boolean;
  size?: "small" | "middle" | "large";
}

const currentYear = dayjs().year();
const startYear = 1950;

const yearOptions = Array.from({ length: currentYear - startYear + 1 }, (_, i) => {
  const year = currentYear - i;
  return { value: year, label: year.toString() };
});

const getDaysInMonth = (month?: number, year?: number): number => {
  if (!month || !year) return 31;
  return dayjs(`${year}-${month}-01`).daysInMonth();
};

export const BirthdaySelect: React.FC<BirthdaySelectProps> = ({
  value,
  onChange,
  disabled,
  size,
}) => {
  const { t } = useTranslation();
  const day = value?.date();
  const month = value?.month() !== undefined && value ? value.month() + 1 : undefined;
  const year = value?.year();

  const monthOptions = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => ({
      value: i + 1,
      label: dayjs().month(i).format("MMMM"),
    }));
  }, []);

  const dayOptions = useMemo(() => {
    const maxDays = getDaysInMonth(month, year);
    return Array.from({ length: maxDays }, (_, i) => ({
      value: i + 1,
      label: (i + 1).toString(),
    }));
  }, [month, year]);

  const handleChange = (
    newDay?: number,
    newMonth?: number,
    newYear?: number,
  ) => {
    const d = newDay ?? day;
    const m = newMonth ?? month;
    const y = newYear ?? year;

    if (d && m && y) {
      const maxDays = getDaysInMonth(m, y);
      const clampedDay = Math.min(d, maxDays);
      onChange?.(dayjs(`${y}-${m}-${clampedDay}`).hour(12));
    } else {
      onChange?.(null);
    }
  };

  return (
    <Row gutter={8}>
      <Col span={8}>
        <Select
          allowClear
          disabled={disabled}
          size={size}
          placeholder={t("Day")}
          value={day}
          options={dayOptions}
          onChange={(v) => handleChange(v ?? undefined, month, year)}
          style={{ width: "100%", textAlign: "left" }}
        />
      </Col>
      <Col span={8}>
        <Select
          allowClear
          disabled={disabled}
          size={size}
          placeholder={t("Month")}
          value={month}
          options={monthOptions}
          onChange={(v) => handleChange(day, v ?? undefined, year)}
          style={{ width: "100%", textAlign: "left" }}
        />
      </Col>
      <Col span={8}>
        <Select
          allowClear
          disabled={disabled}
          size={size}
          placeholder={t("Year")}
          value={year}
          options={yearOptions}
          onChange={(v) => handleChange(day, month, v ?? undefined)}
          style={{ width: "100%", textAlign: "left" }}
        />
      </Col>
    </Row>
  );
};
